import { useMemo } from 'react'

import Vehicle from '../interfaces/Vehicle'
import { getVehiclePublicationStatus } from '../utils/vehiclePublishedUtils'
import { getCommonBodyTypes, getCommonFuelTypes, getCommonColors } from '../utils/strictValidationMappings'
import { getBodiesByType } from '../utils/utils'
import VehicleBody from '../interfaces/VehicleBody'
import BodyByType from '../interfaces/BodyByType'

export interface StrictValidationOptions {
  isStrictMode: boolean
  bodyTypeOptions: BodyByType[]
  fuelTypeOptions: { fuel: string; description: string }[]
  colorOptions: string[]
  shouldShowWarning: boolean
  publishedPlatforms: string[]
}

export interface StrictValidationHandlers {
  convertFormValueToSaveValue: (fieldName: string, value: string) => string
  convertSaveValueToFormValue: (fieldName: string, value: string) => string
}

/**
 * Custom hook for strict validation when vehicles are published to platforms
 * @param vehicle - The vehicle object to check publication status
 * @param vehicleType - Current vehicle type for body filtering
 * @param userBodies - User's available body types from userData
 * @returns Strict validation options and handlers
 */
export const useStrictValidation = (
  vehicle: Vehicle | null,
  vehicleType: string,
  userBodies: VehicleBody[] | undefined
): StrictValidationOptions & StrictValidationHandlers => {
  const publicationStatus = useMemo(() => {
    if (!vehicle)
      return {
        isPublishedToAny: false,
        isPublishedToYapo: false,
        isPublishedToChileAutos: false,
        isPublishedToMercadoLibre: false,
        publishedPlatforms: [],
      }

    return getVehiclePublicationStatus(vehicle)
  }, [vehicle])

  const isStrictMode = publicationStatus.isPublishedToAny

  const bodyTypeOptions = useMemo(() => {
    if (!isStrictMode) {
      // Use regular body types when not in strict mode
      if (userBodies && vehicleType) {
        return getBodiesByType(vehicleType, userBodies)
      }

      return []
    }

    // Use common body types when in strict mode
    const commonBodyTypes = getCommonBodyTypes()

    return commonBodyTypes.map((mapping) => ({
      body: mapping.enum, // Use ENUM for underlying value
      description: mapping.common, // Use common string for display
    }))
  }, [isStrictMode, userBodies, vehicleType])

  const fuelTypeOptions = useMemo(() => {
    if (!isStrictMode) {
      // Return empty array when not in strict mode (regular fuelTranslate will be used)
      return []
    }

    // Use common fuel types when in strict mode
    const commonFuelTypes = getCommonFuelTypes()

    return commonFuelTypes.map((mapping) => ({
      fuel: mapping.enum, // Use ENUM for underlying value
      description: mapping.common, // Use common string for display
    }))
  }, [isStrictMode])

  const colorOptions = useMemo(() => {
    if (!isStrictMode) {
      // Return empty array when not in strict mode (regular text input will be used)
      return []
    }

    // Use common colors when in strict mode
    return getCommonColors()
  }, [isStrictMode])

  const convertFormValueToSaveValue = (_fieldName: string, value: string): string => {
    // ENUMs are now used as values even in strict mode, so no conversion is needed
    // The backend handles platform-specific mappings during API calls
    return value
  }

  const convertSaveValueToFormValue = (_fieldName: string, value: string): string => {
    if (!isStrictMode) {
      return value // No conversion needed when not in strict mode
    }

    // When in strict mode, we expect system ENUMs in the database
    // We can return them directly since the dropdowns now use ENUMs as values
    return value
  }

  return {
    isStrictMode,
    bodyTypeOptions,
    fuelTypeOptions,
    colorOptions,
    shouldShowWarning: isStrictMode,
    publishedPlatforms: publicationStatus.publishedPlatforms,
    convertFormValueToSaveValue,
    convertSaveValueToFormValue,
  }
}
